import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A refresh puts an admin back where they were.
 *
 * The dashboard is a single-page app, so every screen is drawn after the
 * document has loaded and is short at the moment the browser tries to restore.
 * The restore is clamped to whatever exists then — which is the top — so
 * refreshing halfway down a product list, an order, or a long form threw the
 * position away every time. On a screen somebody is working through, that is
 * the difference between carrying on and finding your place again.
 *
 * It remembers the position itself and puts it back once the page is actually
 * tall enough to hold it. The browser's own restoration is left alone, so
 * back and forward behave as they always did.
 *
 * Three things keep it from being annoying:
 *
 *   - It only acts on a reload or a back/forward. Following a link or opening a
 *     new screen starts at the top, which is what anybody expects.
 *   - It stops the moment the admin scrolls, touches or types.
 *   - It gives up after a few seconds regardless: a screen that never grows
 *     back to its old height is one whose content has genuinely changed, and
 *     jumping to the bottom of it is worse than staying put.
 */

const keyFor = (path) => `ks-admin-scroll:${path}`;

/** How long to keep waiting for the screen to grow back. */
const RESTORE_WINDOW_MS = 6000;

/** Below this, "where you were" is the top and restoring is only a flicker. */
const WORTH_RESTORING_PX = 150;

/**
 * The screen this document actually opened on.
 *
 * Read once, when the module is first evaluated. Only that screen may be
 * restored — moving to another one afterwards is a fresh request and starts at
 * the top, which is what anybody expects.
 *
 * This used to be a one-shot flag that the first effect run cleared. React runs
 * effects twice in development and cleans up in between, so the first run
 * consumed the flag and its own cleanup cancelled the restore, leaving the
 * second run with nothing to do — the restore never happened at all. A value
 * that is read rather than consumed cannot be spent by a run that is thrown
 * away.
 */
const landedPath =
  typeof window === 'undefined' ? null : window.location.pathname;

const isReturningNavigation = () => {
  try {
    const [entry] = performance.getEntriesByType('navigation');
    return entry?.type === 'reload' || entry?.type === 'back_forward';
  } catch (err) {
    return false;
  }
};

export default function ScrollRestoration() {
  const { pathname } = useLocation();

  useEffect(() => {
    const key = keyFor(pathname);
    let saveTimer;

    /*
      Written on a timer rather than on every scroll event. A scroll fires
      dozens of times a second and `sessionStorage` is synchronous.
    */
    const remember = () => {
      if (saveTimer) return;
      saveTimer = window.setTimeout(() => {
        saveTimer = undefined;
        try {
          sessionStorage.setItem(key, String(Math.round(window.scrollY)));
        } catch (err) {
          /* storage disabled — nothing to do */
        }
      }, 250);
    };

    const rememberNow = () => {
      try {
        sessionStorage.setItem(key, String(Math.round(window.scrollY)));
      } catch (err) {
        /* ignore */
      }
    };

    window.addEventListener('scroll', remember, { passive: true });
    window.addEventListener('pagehide', rememberNow);

    const shouldRestore = pathname === landedPath && isReturningNavigation();

    let stop = () => {};

    if (shouldRestore) {
      let wanted = 0;
      try {
        wanted = Number(sessionStorage.getItem(key) || 0);
      } catch (err) {
        wanted = 0;
      }

      if (wanted > WORTH_RESTORING_PX) {
        let cancelled = false;
        let frame = 0;
        const deadline = Date.now() + RESTORE_WINDOW_MS;

        const giveUp = () => {
          cancelled = true;
        };
        const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
        events.forEach((name) =>
          window.addEventListener(name, giveUp, { passive: true, once: true }),
        );

        const tick = () => {
          if (cancelled) return;
          const reachable =
            document.documentElement.scrollHeight - window.innerHeight >=
            wanted - 2;

          if (reachable) {
            if (Math.abs(window.scrollY - wanted) > 4) {
              window.scrollTo(0, wanted);
            }
            /* A beat longer: a late table or chart can move the ground under a
               restore that was correct when it happened. */
            if (Date.now() < deadline - RESTORE_WINDOW_MS + 1200) {
              frame = window.requestAnimationFrame(tick);
              return;
            }
            cancelled = true;
            return;
          }
          if (Date.now() > deadline) {
            cancelled = true;
            return;
          }
          frame = window.requestAnimationFrame(tick);
        };

        frame = window.requestAnimationFrame(tick);
        stop = () => {
          cancelled = true;
          window.cancelAnimationFrame(frame);
          events.forEach((name) => window.removeEventListener(name, giveUp));
        };
      }
    }

    return () => {
      stop();
      if (saveTimer) window.clearTimeout(saveTimer);
      window.removeEventListener('scroll', remember);
      window.removeEventListener('pagehide', rememberNow);
    };
  }, [pathname]);

  return null;
}
